
import { useState } from 'react'
import { MoreHorizontal, Copy, Trash2, Edit, Users, Calendar } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useDietPlansV2, useDeleteDietPlanV2, useDuplicateDietPlanV2 } from '@/api/diet-plans-v2'
import type { DietPlanV2 } from '@/types/diet-v2'
import { TableEmptyRow, TableLoadingRows } from '@/components/shared/table-states'

interface DietPlanTableV2Props {
  filters?: {
    duration?: string
    type?: string
    isTemplate?: boolean
  }
  onEdit?: (plan: DietPlanV2) => void
  onAssign?: (plan: DietPlanV2) => void
}

export function DietPlanTableV2({ filters, onEdit, onAssign }: DietPlanTableV2Props) {
  const { data, isLoading } = useDietPlansV2(filters)
  const plans = data?.data || []
  const deleteMutation = useDeleteDietPlanV2()
  const duplicateMutation = useDuplicateDietPlanV2()

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this diet plan?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleDuplicate = async (id: string, name: string) => {
    await duplicateMutation.mutateAsync({ id, name: `${name} (Copy)` })
  }

  const durationLabels = {
    day: 'Single Day',
    week: '7 Days',
    month: '30 Days',
  }

  const typeLabels = {
    'weight-loss': 'Weight Loss',
    'muscle-gain': 'Muscle Gain',
    maintenance: 'Maintenance',
    custom: 'Custom',
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead>Avg Calories/Day</TableHead>
          <TableHead>Macros (P/C/F)</TableHead>
          <TableHead>Template</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableLoadingRows colSpan={7} />
        ) : plans.length === 0 ? (
          <TableEmptyRow colSpan={7} message="No diet plans found. Create your first one!" />
        ) : (
          plans.map((plan) => (
            <TableRow key={plan._id}>
              <TableCell className="font-medium">{plan.name}</TableCell>
              <TableCell>
                <Badge variant="outline">{typeLabels[plan.type]}</Badge>
              </TableCell>
              <TableCell>{durationLabels[plan.duration]}</TableCell>
              <TableCell>{plan.totalCalories} kcal</TableCell>
              <TableCell>
                <div className="text-sm">
                  P: {plan.macros.protein}g / C: {plan.macros.carbs}g / F: {plan.macros.fat}g
                </div>
              </TableCell>
              <TableCell>
                {plan.isTemplate ? <Badge>Template</Badge> : <Badge variant="secondary">User-Specific</Badge>}
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onEdit && (
                      <DropdownMenuItem onClick={() => onEdit(plan)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {onAssign && (
                      <DropdownMenuItem onClick={() => onAssign(plan)}>
                        <Calendar className="h-4 w-4 mr-2" />
                        Assign to User
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => handleDuplicate(plan._id, plan.name)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDelete(plan._id)} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  )
}
